import React from 'react';
import {
  Button,
  Content,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  TextArea,
  TextInput,
} from '@patternfly/react-core';
import { PlusCircleIcon, TrashIcon } from '@patternfly/react-icons';
import type { Zone } from '../../../utils/twigZones';
import type { NewsletterContent } from '../../../types/models/Newsletter';

interface NewsletterZoneEditorProps {
  zones: Zone[];
  content: NewsletterContent;
  onChange: (next: NewsletterContent) => void;
  isDisabled?: boolean;
}

/** `hero_title` → `Hero title`. */
const prettyLabel = (name: string): string => {
  const spaced = name.replace(/[_-]+/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const asString = (value: unknown): string => (value === null || value === undefined ? '' : String(value));

/**
 * NewsletterZoneEditor — renders one field per editable zone discovered in the selected template's
 * Twig (see {@link extractZones}):
 *   • `text` → a single-line input;
 *   • `html` → a monospace textarea (prose zones the template opts into with `|raw`);
 *   • `list` → a repeatable set of rows — one input per row for a scalar list, or one input per
 *     detected sub-field for a list of objects.
 *
 * `content` is the single source of truth (a flat `{ zone: value }` bag). Keys not covered by a
 * detected zone are preserved untouched by the parent, so nothing an existing newsletter stored is
 * lost when the parser doesn't surface it as its own field.
 */
function NewsletterZoneEditor({ zones, content, onChange, isDisabled }: NewsletterZoneEditorProps) {
  const setValue = (name: string, value: unknown) => {
    onChange({ ...content, [name]: value });
  };

  const renderTextZone = (zone: Zone) => (
    <TextInput
      id={`zone-${zone.name}`}
      type="text"
      value={asString(content[zone.name])}
      onChange={(_event, value) => setValue(zone.name, value)}
      isDisabled={isDisabled}
    />
  );

  const renderHtmlZone = (zone: Zone) => (
    <TextArea
      id={`zone-${zone.name}`}
      aria-label={`${prettyLabel(zone.name)} (HTML)`}
      value={asString(content[zone.name])}
      onChange={(_event, value) => setValue(zone.name, value)}
      isDisabled={isDisabled}
      resizeOrientation="vertical"
      rows={6}
      style={{ fontFamily: 'var(--pf-t--global--font--family--mono, monospace)' }}
    />
  );

  const renderListZone = (zone: Zone) => {
    const raw = content[zone.name];
    const items: unknown[] = Array.isArray(raw) ? raw : [];
    const hasFields = zone.itemFields.length > 0;

    const updateItems = (next: unknown[]) => setValue(zone.name, next);

    const addItem = () => updateItems([...items, hasFields ? {} : '']);
    const removeItem = (index: number) => updateItems(items.filter((_, i) => i !== index));

    const setScalar = (index: number, value: string) =>
      updateItems(items.map((item, i) => (i === index ? value : item)));

    const setField = (index: number, field: string, value: string) =>
      updateItems(
        items.map((item, i) => {
          if (i !== index) {
            return item;
          }
          const obj = item !== null && typeof item === 'object' ? (item as Record<string, unknown>) : {};
          return { ...obj, [field]: value };
        }),
      );

    return (
      <div>
        {items.length === 0 && (
          <Content component="small" className="pf-v6-u-mb-sm" style={{ color: 'var(--pf-t--global--text--color--subtle)' }}>
            No items yet.
          </Content>
        )}
        {items.map((item, index) => (
          <div
            key={index}
            className="pf-v6-u-mb-sm pf-v6-u-p-sm"
            style={{ border: '1px solid var(--pf-t--global--border--color--default)', borderRadius: '4px' }}
          >
            <div className="pf-v6-u-display-flex pf-v6-u-align-items-flex-start" style={{ gap: '8px' }}>
              <div style={{ flex: 1 }}>
                {hasFields ? (
                  zone.itemFields.map((field) => (
                    <FormGroup key={field} label={prettyLabel(field)} fieldId={`zone-${zone.name}-${index}-${field}`}>
                      <TextInput
                        id={`zone-${zone.name}-${index}-${field}`}
                        type="text"
                        value={asString((item as Record<string, unknown> | null)?.[field])}
                        onChange={(_event, value) => setField(index, field, value)}
                        isDisabled={isDisabled}
                      />
                    </FormGroup>
                  ))
                ) : (
                  <TextInput
                    id={`zone-${zone.name}-${index}`}
                    type="text"
                    aria-label={`${prettyLabel(zone.name)} item ${index + 1}`}
                    value={asString(item)}
                    onChange={(_event, value) => setScalar(index, value)}
                    isDisabled={isDisabled}
                  />
                )}
              </div>
              <Button
                variant="plain"
                aria-label={`Remove ${prettyLabel(zone.name)} item ${index + 1}`}
                icon={<TrashIcon />}
                onClick={() => removeItem(index)}
                isDisabled={isDisabled}
              />
            </div>
          </div>
        ))}
        <Button variant="link" icon={<PlusCircleIcon />} onClick={addItem} isDisabled={isDisabled} isInline>
          Add item
        </Button>
      </div>
    );
  };

  const zoneHint = (zone: Zone): string => {
    if (zone.kind === 'html') {
      return 'Rich HTML — this zone is rendered unescaped.';
    }
    if (zone.kind === 'list') {
      return zone.itemFields.length > 0
        ? `Repeatable list; each item has: ${zone.itemFields.join(', ')}.`
        : 'Repeatable list of values.';
    }
    return '';
  };

  return (
    <>
      {zones.map((zone) => {
        const hint = zoneHint(zone);
        return (
          <FormGroup
            key={zone.name}
            label={prettyLabel(zone.name)}
            fieldId={`zone-${zone.name}`}
            // A list zone renders a composite of per-item inputs (not one control with id
            // `zone-<name>`), so make the group a real labelled group rather than a dangling
            // <label for> pointing at nothing.
            role={zone.kind === 'list' ? 'group' : undefined}
          >
            {zone.kind === 'text' && renderTextZone(zone)}
            {zone.kind === 'html' && renderHtmlZone(zone)}
            {zone.kind === 'list' && renderListZone(zone)}
            {hint !== '' && (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem>{hint}</HelperTextItem>
                </HelperText>
              </FormHelperText>
            )}
          </FormGroup>
        );
      })}
    </>
  );
}

export default NewsletterZoneEditor;
